import type { ReactElement, ReactNode } from 'react';
import { Box, FormLabel } from '@ksyos/design-system';
import { cloneElement } from 'react';
import { InfoTooltip } from '../../elements/common';

export function Header(props: {
    onSearch?: () => void;
    searchFields?: ReactElement<{ key: number }>[];
    smallHeader?: boolean;
    text: string;
}) {
    return (
        <header>
            <div className="row">
                <div className="col-md-22 col-xl-22 offset-md-1">
                    <div className="justify-content-md-center clearfix">
                        <h1 className={props.smallHeader ? 'small' : ''}>{props.text}</h1>
                    </div>
                </div>
                <div className="col-md-22 col-xl-22 offset-md-1">
                    <div className="justify-content-md-center clearfix">
                        {props.searchFields && (
                            <form
                                className="admin-processes"
                                onSubmit={(e) => {
                                    e.preventDefault();
                                    if (props.onSearch) {
                                        props.onSearch();
                                    }
                                }}
                            >
                                {props.searchFields.map((formGroup, i) => {
                                    return cloneElement(formGroup, { key: i });
                                })}
                            </form>
                        )}
                    </div>
                </div>
            </div>
        </header>
    );
}

export function FormGroup(props: { containers?: ReactElement<{ key: number }>[] }) {
    return (
        <div className="form-group">
            {props.containers && (
                <div className="row">
                    {props.containers.map((container, i) => {
                        return cloneElement(container, { key: i });
                    })}
                </div>
            )}
        </div>
    );
}

export function Container(props: {
    firstInputField: ReactNode;
    label?: string;
    labelToolTipText?: string;
    large?: boolean;
    secondInputField?: ReactNode;
}) {
    if (props.secondInputField) {
        return (
            <div className={props.large ? 'col-md-18' : 'col-md-9'}>
                <div className="row">
                    {props.label && (
                        <div className="col-md-24">
                            <label
                                className="form-label"
                                data-dd-privacy="allow"
                            >
                                {props.label}
                                {props.labelToolTipText && <InfoTooltip text={props.labelToolTipText} />}
                            </label>
                        </div>
                    )}
                    <div className="col-md-12">{props.firstInputField}</div>
                    <div className="col-md-12">{props.secondInputField}</div>
                </div>
            </div>
        );
    } else {
        return (
            <div className={props.large ? 'col-md-9' : 'col-md-6'}>
                <div className="row">
                    {props.label && (
                        <div className="col-md-24">
                            <label
                                className="form-label"
                                data-dd-privacy="allow"
                            >
                                {props.label}
                                {props.labelToolTipText && <InfoTooltip text={props.labelToolTipText} />}
                            </label>
                        </div>
                    )}
                    <div className="col-md-24">{props.firstInputField}</div>
                </div>
            </div>
        );
    }
}

interface ISelectPeriodProps {
    endDate: ReactElement<{ key: number }>;
    endDateLabel?: string;
    startDate: ReactElement<{ key: number }>;
    startDateLabel?: string;
}

export function SelectPeriod({ endDate, startDate, endDateLabel, startDateLabel }: ISelectPeriodProps) {
    return (
        <Box
            alignItems="center"
            display="flex"
            gap={2}
            justifyContent="start"
        >
            {startDateLabel && <FormLabel>{startDateLabel}</FormLabel>}
            {startDate}
            {endDateLabel && <FormLabel>{endDateLabel}</FormLabel>}
            {endDate}
        </Box>
    );
}

export function Form(props: { children: ReactNode }) {
    return (
        <div className="row">
            <div className="col-md-22 offset-md-1">
                <form>{props.children}</form>
            </div>
        </div>
    );
}

export function CheckBox(props: {
    checked?: boolean;
    hide?: boolean;
    onChange: (value: boolean) => void;
    stopPropagation?: boolean;
}) {
    // Remove the style prop and the hide parameter in the props in CS-5207
    return (
        <div
            className="form-check"
            style={props.hide ? { visibility: 'hidden' } : {}}
        >
            {/* eslint-disable-next-line jsx-a11y/label-has-associated-control, jsx-a11y/no-noninteractive-element-interactions */}
            <label
                className="custom-control custom-checkbox"
                data-dd-privacy="allow"
                onClick={(e) => props.stopPropagation && e.stopPropagation()}
            >
                <input
                    checked={props.checked}
                    className="custom-control-input"
                    onChange={(e) => props.onChange(e.target.checked)}
                    type="checkbox"
                />
                <span className="custom-control-indicator">&nbsp;</span>
            </label>
        </div>
    );
}

export function ProcessesFooterButtons(props: { buttons?: ReactElement<{ key: number }>[]; children?: ReactNode }) {
    return (
        <div className="d-flex justify-content-md-center">
            <div className="btn-container">
                {props.buttons && props.buttons.map((button, i) => cloneElement(button, { key: i }))}
                {props.children && <>{props.children}</>}
            </div>
        </div>
    );
}
